/**
 * ========================================================
 * 🍽️ BASE DE DATOS DE RECETAS - MEALCYCLE v5.1 (CORREGIDO)
 * ========================================================
 * ✅ FIX: Nombres de componentes corregidos para match exacto con sampleComponents.js
 * ✅ ARQUITECTURA MODULAR: Usa componentes reutilizables (Legos).
 * ✅ MÁXIMA FLEXIBILIDAD: Pastas, salsas y guarniciones separadas.
 * ✅ +80 RECETAS ARGENTINAS: Combinaciones reales de hogar y bodegón.
 * ✅ MATCH EXACTO: Porciones (servings) calibradas para cálculo exacto de compras.
 * ✅ UX OPTIMIZADA: Opciones suficientes para no cargar nada manual el Día 1.
 * 166 recetas
 */
import { 
  COMPONENT_TYPES, 
  CATEGORIES, 
  UNITS 
} from './types';

// ============================================
// CATEGORÍAS DE RECETAS
// ============================================

export const RECIPE_CATEGORIES = {
  TRADICIONAL: 'tradicional',
  RAPIDA: 'rapida',
  SALUDABLE: 'saludable',
  ECONOMICA: 'economica',
  VEGETARIANA: 'vegetariana',
  DESAYUNO: 'desayuno',
  ALMUERZO: 'almuerzo',
  CENA: 'cena',
  MERIENDA: 'merienda'
};

export const RECIPE_CATEGORY_LABELS = {
  [RECIPE_CATEGORIES.TRADICIONAL]: 'Tradicional',
  [RECIPE_CATEGORIES.RAPIDA]: 'Rápida',
  [RECIPE_CATEGORIES.SALUDABLE]: 'Saludable',
  [RECIPE_CATEGORIES.ECONOMICA]: 'Económica',
  [RECIPE_CATEGORIES.VEGETARIANA]: 'Vegetariana',
  [RECIPE_CATEGORIES.DESAYUNO]: 'Desayuno',
  [RECIPE_CATEGORIES.ALMUERZO]: 'Almuerzo',
  [RECIPE_CATEGORIES.CENA]: 'Cena',
  [RECIPE_CATEGORIES.MERIENDA]: 'Merienda'
};

export const RECIPE_CATEGORY_ICONS = {
  [RECIPE_CATEGORIES.TRADICIONAL]: '🍽️',
  [RECIPE_CATEGORIES.RAPIDA]: '⚡',
  [RECIPE_CATEGORIES.SALUDABLE]: '🥗',
  [RECIPE_CATEGORIES.ECONOMICA]: '💰',
  [RECIPE_CATEGORIES.VEGETARIANA]: '🌱',
  [RECIPE_CATEGORIES.DESAYUNO]: '🌅',
  [RECIPE_CATEGORIES.ALMUERZO]: '☀️',
  [RECIPE_CATEGORIES.CENA]: '🌙',
  [RECIPE_CATEGORIES.MERIENDA]: '☕'
};

// ============================================
// CÁLCULO DE INGREDIENTES
// ============================================

export const calculateRecipeIngredients = (components, recipeServings) => {
  const ingredientMap = {};
  
  components.forEach(component => {
    const componentServings = component.servingsInRecipe || 1;
    const multiplier = (recipeServings * componentServings) / (component.servings || 1);
    
    component.ingredients.forEach(ing => {
      const key = `${ing.name.toLowerCase()}_${ing.unit}`;
      
      if (!ingredientMap[key]) {
        ingredientMap[key] = {
          name: ing.name,
          quantity: 0,
          unit: ing.unit,
          category: ing.category || 'otros'
        };
      }
      
      ingredientMap[key].quantity += ing.quantity * multiplier;
    });
  });
  
  return Object.values(ingredientMap).map(ing => ({
    ...ing,
    quantity: Math.round(ing.quantity * 100) / 100
  }));
};

// ============================================
// VALIDACIÓN
// ============================================

export const validateRecipe = (recipe) => {
  const errors = [];
  
  if (!recipe.name || recipe.name.trim().length === 0) {
    errors.push('El nombre es obligatorio');
  }
  
  if (recipe.name && recipe.name.length > 50) {
    errors.push('El nombre no puede tener más de 50 caracteres');
  }
  
  if (!recipe.category) {
    errors.push('La categoría es obligatoria');
  }
  
  if (!recipe.servings || recipe.servings <= 0) {
    errors.push('El número de porciones debe ser mayor a 0');
  }
  
  if (recipe.servings && recipe.servings > 20) {
    errors.push('El número de porciones no puede ser mayor a 20');
  }
  
  if (!recipe.main) {
    errors.push('Debe tener un componente principal');
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

// ============================================
// RECETAS COMPLETAS (60+)
// ✅ ACTUALIZADAS CON COMPONENTES SIMPLIFICADOS
// ============================================

export const SAMPLE_RECIPES = [
  
  // ==========================================
  // 🌅 DESAYUNOS (7 recetas)
  // ==========================================
  
  {
    id: 'rec_cafe_facturas',
    name: 'Café con Facturas',
    category: RECIPE_CATEGORIES.DESAYUNO,
    servings: 1,
    main: {
      componentId: 'comp_cafe_leche',
      componentName: 'Café con Leche',
      servings: 1
    },
    sides: [
      {
        componentId: 'comp_facturas',
        componentName: 'Facturas',
        servings: 1
      }
    ],
    ingredients: []
  },
  
  {
    id: 'rec_cafe_medialunas',
    name: 'Café con Medialunas',
    category: RECIPE_CATEGORIES.DESAYUNO,
    servings: 1,
    main: {
      componentId: 'comp_cafe_leche',
      componentName: 'Café con Leche',
      servings: 1
    },
    sides: [
      {
        componentId: 'comp_medialunas',
        componentName: 'Medialunas',
        servings: 1
      }
    ],
    ingredients: []
  },
  
  {
    id: 'rec_desayuno_completo',
    name: 'Desayuno Completo',
    category: RECIPE_CATEGORIES.DESAYUNO,
    servings: 1,
    main: {
      componentId: 'comp_cafe_leche',
      componentName: 'Café con Leche',
      servings: 1
    },
    sides: [
      {
        componentId: 'comp_tostadas_dulce',
        componentName: 'Tostadas con Dulce',
        servings: 1
      },
      {
        componentId: 'comp_jugo_naranja',
        componentName: 'Jugo de Naranja',
        servings: 1
      }
    ],
    ingredients: []
  },
  
  {
    id: 'rec_mate_cocido_tostadas',
    name: 'Mate Cocido con Tostadas',
    category: RECIPE_CATEGORIES.DESAYUNO,
    servings: 1,
    main: {
      componentId: 'comp_mate_cocido',
      componentName: 'Mate Cocido',
      servings: 1
    },
    sides: [
      {
        componentId: 'comp_tostadas_manteca',
        componentName: 'Tostadas con Manteca',
        servings: 1
      }
    ],
    ingredients: []
  },
  {
    id: 'rec_mate_galletitas', 
    name: 'Mate con Galletitas',
    category: RECIPE_CATEGORIES.DESAYUNO,
    servings: 1,
    main: {
      componentId: 'comp_mate', 
      componentName: 'Mate',
      servings: 1
    },
    sides: [
      {
        componentId: 'comp_galletitas',
        componentName: 'Galletitas',
        servings: 1
      }
    ],
    ingredients: []
  },
  
  {
    id: 'rec_tostado_licuado',
    name: 'Tostado con Licuado',
    category: RECIPE_CATEGORIES.DESAYUNO,
    servings: 1,
    main: {
      componentId: 'comp_tostado_jyq',
      componentName: 'Tostado',
      servings: 1
    },
    sides: [
      {
        componentId: 'comp_licuado_banana',
        componentName: 'Licuado de Banana',
        servings: 1
      }
    ],
    ingredients: []
  },
  
  {
    id: 'rec_yogur_granola',
    name: 'Yogur con Granola y Frutas',
    category: RECIPE_CATEGORIES.DESAYUNO,
    servings: 1,
    main: {
      componentId: 'comp_yogur_granola', 
      componentName: 'Yogur con Granola',
      servings: 1
    },
    sides: [
      {
        componentId: 'comp_ensalada_frutas',
        componentName: 'Ensalada de Frutas',
        servings: 1
      }
    ],
    ingredients: []
  },
  
  // ==========================================
  // ☕ MERIENDAS (4 recetas)
  // ==========================================
  
  {
    id: 'rec_mate_facturas',
    name: 'Mate con Facturas',
    category: RECIPE_CATEGORIES.MERIENDA,
    servings: 1,
    main: {
      componentId: 'comp_mate',
      componentName: 'Mate',
      servings: 1
    },
    sides: [
      {
        componentId: 'comp_facturas',
        componentName: 'Facturas',
        servings: 1
      }
    ],
    ingredients: []
  },
  
  {
    id: 'rec_te_panqueques',
    name: 'Té con Panqueques',
    category: RECIPE_CATEGORIES.MERIENDA,
    servings: 1,
    main: {
      componentId: 'comp_te_leche',
      componentName: 'Té con Leche',
      servings: 1
    },
    sides: [
      {
        componentId: 'comp_panqueques',
        componentName: 'Panqueques',
        servings: 1
      }
    ],
    ingredients: []
  },
  
  {
    id: 'rec_te_tostadas',
    name: 'Té con Tostadas',
    category: RECIPE_CATEGORIES.MERIENDA,
    servings: 1,
    main: {
      componentId: 'comp_te_leche',
      componentName: 'Té con Leche',
      servings: 1
    },
    sides: [
      {
        componentId: 'comp_tostadas_mermelada',
        componentName: 'Tostadas con Mermelada',
        servings: 1
      }
    ],
    ingredients: []
  },
  {
    id: 'rec_chocolatada_galletitas',
    name: 'Chocolatada con Galletitas',
    category: RECIPE_CATEGORIES.MERIENDA,
    servings: 1,
    main: {
      componentId: 'comp_leche_chocolatada',
      componentName: 'Leche Chocolatada',
      servings: 1
    },
    sides: [
      {
        componentId: 'comp_galletitas',
        componentName: 'Galletitas',
        servings: 1
      }
    ],
    ingredients: []
  },
  
  // ==========================================
  // 🥟 TARTAS Y EMPANADAS (8 recetas)
  // ==========================================
  
  {
    id: 'rec_tarta_espinaca',
    name: 'Tarta de Espinaca',
    category: RECIPE_CATEGORIES.VEGETARIANA,
    servings: 4,
    main: {
      componentId: 'comp_tarta_espinaca',
      componentName: 'Tarta de Espinaca',
      servings: 1
    },
    sides: [
      {
        componentId: 'comp_ensalada_verde',
        componentName: 'Ensalada Verde',
        servings: 1
      }
    ],
    ingredients: []
  },
  
  {
    id: 'rec_tarta_pollo',
    name: 'Tarta de Pollo',
    category: RECIPE_CATEGORIES.TRADICIONAL,
    servings: 4,
    main: {
      componentId: 'comp_tarta_pollo',
      componentName: 'Tarta de Pollo',
      servings: 1
    },
    sides: [
      {
        componentId: 'comp_ensalada_mixta',
        componentName: 'Ensalada Mixta',
        servings: 1
      }
    ],
    ingredients: []
  },
  
  {
    id: 'rec_tarta_atun',
    name: 'Tarta de Atún',
    category: RECIPE_CATEGORIES.RAPIDA,
    servings: 4,
    main: {
      componentId: 'comp_tarta_atun',
      componentName: 'Tarta de Atún',
      servings: 1
    },
    sides: [
      {
        componentId: 'comp_ensalada_verde',
        componentName: 'Ensalada Verde',
        servings: 1
      }
    ],
    ingredients: []
  },
  
  {
    id: 'rec_empanadas_carne',
    name: 'Empanadas de Carne',
    category: RECIPE_CATEGORIES.TRADICIONAL,
    servings: 2,
    main: {
      componentId: 'comp_empanada_carne',
      componentName: 'Empanadas de Carne (Horno/Fritas)', // ✅ CORREGIDO
      servings: 1
    },
    sides: [
      {
        componentId: 'comp_ensalada_criolla',
        componentName: 'Ensalada Criolla',
        servings: 1
      }
    ],
    ingredients: []
  },
  
  {
    id: 'rec_empanadas_jyq',
    name: 'Empanadas de Jamón y Queso',
    category: RECIPE_CATEGORIES.RAPIDA,
    servings: 2,
    main: {
      componentId: 'comp_empanada_jyq',
      componentName: 'Empanadas de Jamón y Queso', // ✅ CORREGIDO
      servings: 1
    },
    sides: [
      {
        componentId: 'comp_ensalada_mixta',
        componentName: 'Ensalada Mixta',
        servings: 1
      }
    ],
    ingredients: []
  },
  
  {
    id: 'rec_empanadas_arabes',
    name: 'Empanadas Árabes',
    category: RECIPE_CATEGORIES.TRADICIONAL,
    servings: 2,
    main: {
      componentId: 'comp_empanada_arabe',
      componentName: 'Empanadas Árabes (Fatay)', // ✅ CORREGIDO
      servings: 1
    },
    sides: [
      {
        componentId: 'comp_ensalada_verde',
        componentName: 'Ensalada Verde',
        servings: 1
      }
    ],
    ingredients: []
  },
  
  {
    id: 'rec_pastel_papa',
    name: 'Pastel de Papa',
    category: RECIPE_CATEGORIES.TRADICIONAL,
    servings: 6,
    main: {
      componentId: 'comp_pastel_papa',
      componentName: 'Pastel de Papa',
      servings: 1
    },
    sides: [
      {
        componentId: 'comp_ensalada_mixta',
        componentName: 'Ensalada Mixta',
        servings: 1
      }
    ],
    ingredients: []
  },
  
  {
    id: 'rec_pascualina',
    name: 'Pascualina',
    category: RECIPE_CATEGORIES.VEGETARIANA,
    servings: 4,
    main: {
      componentId: 'comp_pascualina',
      componentName: 'Tarta Pascualina (Acelga)',
      servings: 1
    },
    sides: [
      {
        componentId: 'comp_ensalada_verde',
        componentName: 'Ensalada Verde',
        servings: 1
      }
    ],
    ingredients: []
  },
  
  // ==========================================
  // 🍖 MILANESAS (7 recetas - Múltiples variantes)
  // ==========================================
  
  {
    id: 'rec_milas_pure',
    name: 'Milanesas con Puré',
    category: RECIPE_CATEGORIES.TRADICIONAL,
    servings: 2,
    main: {
      componentId: 'comp_milanesa',
      componentName: 'Milanesa',
      servings: 2
    },
    sides: [
      {
        componentId: 'comp_pure',
        componentName: 'Puré de Papa',
        servings: 2
      }
    ],
    ingredients: []
  },
  
  {
    id: 'rec_milas_brocoli',
    name: 'Milanesas con Ensalada de Brócoli',
    category: RECIPE_CATEGORIES.SALUDABLE,
    servings: 2,
    main: {
      componentId: 'comp_milanesa',
      componentName: 'Milanesa',
      servings: 2
    },
    sides: [
      {
        componentId: 'comp_ensalada_brocoli',
        componentName: 'Ensalada de Brócoli',
        servings: 2
      }
    ],
    ingredients: []
  },
  
  {
    id: 'rec_milas_chaucha',
    name: 'Milanesas con Ensalada de Chaucha',
    category: RECIPE_CATEGORIES.SALUDABLE,
    servings: 2,
    main: {
      componentId: 'comp_milanesa',
      componentName: 'Milanesa',
      servings: 2
    },
    sides: [
      {
        componentId: 'comp_ensalada_chaucha',
        componentName: 'Ensalada de Chaucha',
        servings: 2
      }
    ],
    ingredients: []
  },
  
  {
    id: 'rec_milas_palta_arroz',
    name: 'Milanesas con Palta y Arroz',
    category: RECIPE_CATEGORIES.SALUDABLE,
    servings: 2,
    main: {
      componentId: 'comp_milanesa',
      componentName: 'Milanesa',
      servings: 2
    },
    sides: [
      {
        componentId: 'comp_palta',
        componentName: 'Palta (Ensalada)',
        servings: 2
      },
      {
        componentId: 'comp_ensalada_tomate',
        componentName: 'Ensalada de Tomate y Huevo',
        servings: 2
      },
      {
        componentId: 'comp_arroz_blanco',
        componentName: 'Arroz Blanco',
        servings: 2
      }
    ],
    ingredients: []
  },
  
  {
    id: 'rec_milas_lenteja',
    name: 'Milanesas con Ensalada de Lentejas',
    category: RECIPE_CATEGORIES.SALUDABLE,
    servings: 2,
    main: {
      componentId: 'comp_milanesa',
      componentName: 'Milanesa',
      servings: 2
    },
    sides: [
      {
        componentId: 'comp_ensalada_lenteja',
        componentName: 'Ensalada de Lentejas',
        servings: 2
      }
    ],
    ingredients: []
  },
  
  {
    id: 'rec_milas_papas',
    name: 'Milanesas con Papas Fritas',
    category: RECIPE_CATEGORIES.TRADICIONAL,
    servings: 2,
    main: {
      componentId: 'comp_milanesa',
      componentName: 'Milanesa',
      servings: 2
    },
    sides: [
      {
        componentId: 'comp_papas_fritas',
        componentName: 'Papas Fritas',
        servings: 2
      },
      {
        componentId: 'comp_ensalada_verde',
        componentName: 'Ensalada Verde',
        servings: 2
      }
    ],
    ingredients: []
  },
  
  {
    id: 'rec_sandwich_milanesa',
    name: 'Sándwich de Milanesa Completo',
    category: RECIPE_CATEGORIES.RAPIDA,
    servings: 1,
    main: {
      componentId: 'comp_sandwich_milanesa',
      componentName: 'Sándwich de Milanesa',
      servings: 1
    },
    sides: [
      {
        componentId: 'comp_papas_fritas',
        componentName: 'Papas Fritas',
        servings: 1
      }
    ],
    ingredients: []
  },
  
  // ==========================================
  // 🥩 CARNES PRINCIPALES (15 recetas)
  // ==========================================
  
  {
    id: 'rec_albondigas_pure',
    name: 'Albóndigas con Puré',
    category: RECIPE_CATEGORIES.TRADICIONAL,
    servings: 4,
    main: {
      componentId: 'comp_albondigas',
      componentName: 'Albóndigas',
      servings: 2
    },
    sides: [
      {
        componentId: 'comp_pure',
        componentName: 'Puré de Papa',
        servings: 2
      },
      {
        componentId: 'comp_salsa_tomate',
        componentName: 'Salsa de Tomate / Fileto',
        servings: 1
      }
    ],
    ingredients: []
  },
  
  {
    id: 'rec_asado',
    name: 'Asado Completo',
    category: RECIPE_CATEGORIES.TRADICIONAL,
    servings: 4,
    main: {
      componentId: 'comp_asado_tira',
      componentName: 'Asado de Tira',
      servings: 4
    },
    sides: [
      {
        componentId: 'comp_ensalada_criolla',
        componentName: 'Ensalada Criolla',
        servings: 2
      },
      {
        componentId: 'comp_chimichurri',
        componentName: 'Chimichurri',
        servings: 1
      }
    ],
    ingredients: []
  },
  
  {
    id: 'rec_bife_ensalada_arroz',
    name: 'Bife con Ensalada de Arroz',
    category: RECIPE_CATEGORIES.TRADICIONAL,
    servings: 2,
    main: {
      componentId: 'comp_bife',
      componentName: 'Bife',
      servings: 2
    },
    sides: [
      {
        componentId: 'comp_ensalada_arroz',
        componentName: 'Ensalada de Arroz',
        servings: 1
      }
    ],
    ingredients: []
  },
  
  {
    id: 'rec_brochetas_arroz',
    name: 'Brochetas de Pollo con Arroz',
    category: RECIPE_CATEGORIES.SALUDABLE,
    servings: 2,
    main: {
      componentId: 'comp_brochetas_pollo',
      componentName: 'Brochetas de Pollo',
      servings: 2
    },
    sides: [
      {
        componentId: 'comp_arroz_blanco',
        componentName: 'Arroz Blanco',
        servings: 2
      },
      {
        componentId: 'comp_ensalada_verde',
        componentName: 'Ensalada Verde',
        servings: 1
      }
    ],
    ingredients: []
  },
  
  {
    id: 'rec_choripan',
    name: 'Choripanes',
    category: RECIPE_CATEGORIES.RAPIDA,
    servings: 2,
    main: {
      componentId: 'comp_choripan',
      componentName: 'Choripán',
      servings: 2
    },
    sides: [
      {
        componentId: 'comp_chimichurri',
        componentName: 'Chimichurri',
        servings: 1
      },
      {
        componentId: 'comp_ensalada_criolla',
        componentName: 'Ensalada Criolla',
        servings: 1
      }
    ],
    ingredients: []
  },
  
  {
    id: 'rec_costillas_ensalada',
    name: 'Costillas con Ensalada',
    category: RECIPE_CATEGORIES.TRADICIONAL,
    servings: 3,
    main: {
      componentId: 'comp_costillas',
      componentName: 'Costillas de Cerdo',
      servings: 2
    },
    sides: [
      {
        componentId: 'comp_ensalada_mixta',
        componentName: 'Ensalada Mixta',
        servings: 1
      }
    ],
    ingredients: []
  },
  
  {
    id: 'rec_hamburguesas',
    name: 'Hamburguesas Caseras',
    category: RECIPE_CATEGORIES.RAPIDA,
    servings: 2,
    main: {
      componentId: 'comp_hamburguesa',
      componentName: 'Hamburguesa (Casera)',
      servings: 2
    },
    sides: [
      {
        componentId: 'comp_papas_fritas',
        componentName: 'Papas Fritas',
        servings: 1
      },
      {
        componentId: 'comp_ensalada_verde',
        componentName: 'Ensalada Verde',
        servings: 1
      }
    ],
    ingredients: []
  },
  
  {
    id: 'rec_marineras_papas',
    name: 'Marineras con Papas Fritas',
    category: RECIPE_CATEGORIES.TRADICIONAL,
    servings: 2,
    main: {
      componentId: 'comp_marinera_rebozada',
      componentName: 'Marinera de Pescado',
      servings: 2
    },
    sides: [
      {
        componentId: 'comp_papas_fritas',
        componentName: 'Papas Fritas',
        servings: 1
      }
    ],
    ingredients: []
  },
  
  {
    id: 'rec_matambre_pizza',
    name: 'Matambre a la Pizza',
    category: RECIPE_CATEGORIES.TRADICIONAL,
    servings: 4,
    main: {
      componentId: 'comp_matambre_pizza',
      componentName: 'Matambre a la Pizza',
      servings: 2
    },
    sides: [
      {
        componentId: 'comp_ensalada_verde',
        componentName: 'Ensalada Verde',
        servings: 1
      }
    ],
    ingredients: []
  },
  
  {
    id: 'rec_medallones_pure',
    name: 'Medallones con Puré',
    category: RECIPE_CATEGORIES.ECONOMICA,
    servings: 4,
    main: {
      componentId: 'comp_medallones',
      componentName: 'Medallones de Carne y Avena',
      servings: 2
    },
    sides: [
      {
        componentId: 'comp_pure',
        componentName: 'Puré de Papa',
        servings: 2
      }
    ],
    ingredients: []
  },
  
  {
    id: 'rec_pechuga_arroz_primavera',
    name: 'Pechuga con Ensalada de Arroz Primavera',
    category: RECIPE_CATEGORIES.SALUDABLE,
    servings: 2,
    main: {
      componentId: 'comp_pollo_grillado',
      componentName: 'Pollo Grillado',
      servings: 2
    },
    sides: [
      {
        componentId: 'comp_ensalada_arroz',
        componentName: 'Ensalada de Arroz',
        servings: 1
      }
    ],
    ingredients: []
  },
  
  {
    id: 'rec_pollo_horno_papas',
    name: 'Pollo al Horno con Papas',
    category: RECIPE_CATEGORIES.TRADICIONAL,
    servings: 4,
    main: {
      componentId: 'comp_pollo_horno',
      componentName: 'Pollo al Horno con Papas',
      servings: 1
    },
    sides: [
      {
        componentId: 'comp_ensalada_verde',
        componentName: 'Ensalada Verde',
        servings: 1
      }
    ],
    ingredients: []
  },
  
  {
    id: 'rec_salmon_brocoli',
    name: 'Pescado Grillado con Brócoli',
    category: RECIPE_CATEGORIES.SALUDABLE,
    servings: 2,
    main: {
      componentId: 'comp_salmon',
      componentName: 'Pescado Grillado',
      servings: 2
    },
    sides: [
      {
        componentId: 'comp_brocoli_vapor',
        componentName: 'Brócoli al Vapor',
        servings: 1
      }
    ],
    ingredients: []
  },
  
  {
    id: 'rec_tacos',
    name: 'Tacos de Carne',
    category: RECIPE_CATEGORIES.RAPIDA,
    servings: 2,
    main: {
      componentId: 'comp_tacos',
      componentName: 'Tacos de Carne',
      servings: 2
    },
    sides: [
      {
        componentId: 'comp_ensalada_verde',
        componentName: 'Ensalada Verde',
        servings: 1
      }
    ],
    ingredients: []
  },
  
  {
    id: 'rec_vacio_ensalada',
    name: 'Vacío con Ensalada',
    category: RECIPE_CATEGORIES.TRADICIONAL,
    servings: 4,
    main: {
      componentId: 'comp_vacio_horno',
      componentName: 'Vacío al Horno',
      servings: 2
    },
    sides: [
      {
        componentId: 'comp_ensalada_criolla',
        componentName: 'Ensalada Criolla',
        servings: 1
      },
      {
        componentId: 'comp_chimichurri',
        componentName: 'Chimichurri',
        servings: 1
      }
    ],
    ingredients: []
  },
  {
    id: 'rec_entrana_pure_mixto',
    name: 'Entraña con Puré Mixto',
    category: RECIPE_CATEGORIES.TRADICIONAL,
    servings: 2,
    main: {
      componentId: 'comp_entraña',
      componentName: 'Entraña',
      servings: 2
    },
    sides: [
      {
        componentId: 'comp_pure_mixto',
        componentName: 'Puré Mixto',
        servings: 2
      }
    ],
    ingredients: []
  },
  {
    id: 'rec_matambre_pizza_fritas',
    name: 'Matambre a la Pizza con Fritas',
    category: RECIPE_CATEGORIES.TRADICIONAL,
    servings: 4,
    main: {
      componentId: 'comp_matambre_pizza',
      componentName: 'Matambre a la Pizza',
      servings: 2
    },
    sides: [
      {
        componentId: 'comp_papas_fritas',
        componentName: 'Papas Fritas',
        servings: 2
      }
    ],
    ingredients: []
  },
  {
    id: 'rec_albondigas_arroz',
    name: 'Albóndigas con Arroz Blanco',
    category: RECIPE_CATEGORIES.ECONOMICA,
    servings: 4,
    main: {
      componentId: 'comp_albondigas',
      componentName: 'Albóndigas',
      servings: 2
    },
    sides: [
      {
        componentId: 'comp_salsa_tomate',
        componentName: 'Salsa de Tomate / Fileto',
        servings: 1
      },
      {
        componentId: 'comp_arroz_blanco',
        componentName: 'Arroz Blanco',
        servings: 2
      }
    ],
    ingredients: []
  },
  {
    id: 'rec_asado_rusa',
    name: 'Asado con Ensalada Rusa',
    category: RECIPE_CATEGORIES.TRADICIONAL,
    servings: 4,
    main: {
      componentId: 'comp_asado_tira',
      componentName: 'Asado de Tira',
      servings: 4
    },
    sides: [
      {
        componentId: 'comp_ensalada_rusa',
        componentName: 'Ensalada Rusa',
        servings: 2
      }
    ],
    ingredients: []
  },
  {
    id: 'rec_bife_zapallo',
    name: 'Bife con Puré de Zapallo',
    category: RECIPE_CATEGORIES.SALUDABLE,
    servings: 2,
    main: {
      componentId: 'comp_bife',
      componentName: 'Bife',
      servings: 2
    },
    sides: [
      {
        componentId: 'comp_pure_zapallo',
        componentName: 'Puré de Zapallo',
        servings: 2
      }
    ],
    ingredients: []
  },
  
  {
    id: 'rec_pollo_grillado_rusa',
    name: 'Pollo Grillado con Ensalada Rusa',
    category: RECIPE_CATEGORIES.SALUDABLE,
    servings: 2,
    main: {
      componentId: 'comp_pollo_grillado',
      componentName: 'Pollo Grillado',
      servings: 2
    },
    sides: [
      {
        componentId: 'comp_ensalada_rusa',
        componentName: 'Ensalada Rusa',
        servings: 2
      }
    ],
    ingredients: []
  },
  {
    id: 'rec_brochetas_papas_horno',
    name: 'Brochetas de Pollo con Papas al Horno',
    category: RECIPE_CATEGORIES.TRADICIONAL,
    servings: 2,
    main: {
      componentId: 'comp_brochetas_pollo',
      componentName: 'Brochetas de Pollo',
      servings: 2
    },
    sides: [
      {
        componentId: 'comp_papas_horno',
        componentName: 'Papas al Horno',
        servings: 1
      }
    ],
    ingredients: []
  },
  {
    id: 'rec_pechugas_rellenas_verde',
    name: 'Pechugas Rellenas con Ensalada Verde',
    category: RECIPE_CATEGORIES.TRADICIONAL,
    servings: 2,
    main: {
      componentId: 'comp_pechugas_rellenas',
      componentName: 'Pechugas Rellenas',
      servings: 2
    },
    sides: [
      {
        componentId: 'comp_ensalada_verde',
        componentName: 'Ensalada Verde',
        servings: 1
      }
    ],
    ingredients: []
  },
  {
    id: 'rec_suprema_pure',
    name: 'Suprema con Puré de Papa',
    category: RECIPE_CATEGORIES.TRADICIONAL,
    servings: 2,
    main: {
      componentId: 'comp_suprema_pollo',
      componentName: 'Suprema de Pollo (Milanesa)',
      servings: 2
    },
    sides: [
      {
        componentId: 'comp_pure',
        componentName: 'Puré de Papa',
        servings: 2
      }
    ],
    ingredients: []
  },
  {
    id: 'rec_milanesa_rusa',
    name: 'Milanesa con Ensalada Rusa',
    category: RECIPE_CATEGORIES.TRADICIONAL,
    servings: 2,
    main: {
      componentId: 'comp_milanesa',
      componentName: 'Milanesa',
      servings: 2
    },
    sides: [
      {
        componentId: 'comp_ensalada_rusa',
        componentName: 'Ensalada Rusa',
        servings: 2
      }
    ],
    ingredients: []
  },
  {
    id: 'rec_bife_pure',
    name: 'Bife a la Plancha con Puré',
    category: RECIPE_CATEGORIES.RAPIDA,
    servings: 2,
    main: {
      componentId: 'comp_bife',
      componentName: 'Bife',
      servings: 2
    },
    sides: [
      {
        componentId: 'comp_pure',
        componentName: 'Puré de Papa',
        servings: 2
      }
    ],
    ingredients: []
  },
  {
    id: 'rec_bife_chorizo_fritas',
    name: 'Bife de Chorizo con Papas Fritas',
    category: RECIPE_CATEGORIES.TRADICIONAL,
    servings: 2,
    main: {
      componentId: 'comp_bife_chorizo',
      componentName: 'Bife de Chorizo',
      servings: 2
    },
    sides: [
      {
        componentId: 'comp_papas_fritas',
        componentName: 'Papas Fritas',
        servings: 2
      }
    ],
    ingredients: []
  },
  {
    id: 'rec_cerdo_batatas',
    name: 'Cerdo a la Mostaza con Batatas Fritas',
    category: RECIPE_CATEGORIES.TRADICIONAL,
    servings: 4,
    main: {
      componentId: 'comp_cerdo_mostaza',
      componentName: 'Cerdo a la Mostaza',
      servings: 1
    },
    sides: [
      {
        componentId: 'comp_batatas_fritas',
        componentName: 'Batatas Fritas',
        servings: 2
      }
    ],
    ingredients: []
  },
  {
    id: 'rec_bondiola_papas',
    name: 'Bondiola a la Cerveza con Papas al Horno',
    category: RECIPE_CATEGORIES.TRADICIONAL,
    servings: 4,
    main: {
      componentId: 'comp_bondiola_cerveza',
      componentName: 'Bondiola a la Cerveza',
      servings: 1
    },
    sides: [
      {
        componentId: 'comp_papas_horno',
        componentName: 'Papas al Horno',
        servings: 2
      }
    ],
    ingredients: []
  },
  {
    id: 'rec_bife_criolla_arroz',
    name: 'Bifes a la Criolla con Arroz Blanco',
    category: RECIPE_CATEGORIES.ECONOMICA,
    servings: 4,
    main: {
      componentId: 'comp_bife_criolla',
      componentName: 'Bifes a la Criolla',
      servings: 1
    },
    sides: [
      {
        componentId: 'comp_arroz_blanco',
        componentName: 'Arroz Blanco',
        servings: 2
      }
    ],
    ingredients: []
  },
  {
    id: 'rec_pata_muslo_pure',
    name: 'Pata Muslo al Horno con Puré',
    category: RECIPE_CATEGORIES.TRADICIONAL,
    servings: 2,
    main: {
      componentId: 'comp_pata_muslo_horno',
      componentName: 'Pata Muslo al Horno',
      servings: 2
    },
    sides: [
      {
        componentId: 'comp_pure',
        componentName: 'Puré de Papa',
        servings: 2
      }
    ],
    ingredients: []
  },
  {
    id: 'rec_pata_muslo_papas',
    name: 'Pata Muslo con Papas al Horno',
    category: RECIPE_CATEGORIES.TRADICIONAL,
    servings: 2,
    main: {
      componentId: 'comp_pata_muslo_horno',
      componentName: 'Pata Muslo al Horno',
      servings: 2
    },
    sides: [
      {
        componentId: 'comp_papas_horno',
        componentName: 'Papas al Horno',
        servings: 2
      }
    ],
    ingredients: []
  },
  {
    id: 'rec_suprema_fritas',
    name: 'Suprema Maryland con Fritas',
    category: RECIPE_CATEGORIES.TRADICIONAL,
    servings: 2,
    main: {
      componentId: 'comp_suprema_maryland',
      componentName: 'Suprema Maryland',
      servings: 2
    },
    sides: [
      {
        componentId: 'comp_papas_fritas',
        componentName: 'Papas Fritas',
        servings: 1
      }
    ],
    ingredients: []
  },
  {
    id: 'rec_pollo_zapallo',
    name: 'Pollo Grillado con Puré de Zapallo',
    category: RECIPE_CATEGORIES.SALUDABLE,
    servings: 2,
    main: {
      componentId: 'comp_pollo_grillado',
      componentName: 'Pollo Grillado',
      servings: 2
    },
    sides: [
      {
        componentId: 'comp_pure_zapallo',
        componentName: 'Puré de Zapallo',
        servings: 2
      }
    ],
    ingredients: []
  },
  {
    id: 'rec_pechugas_rellenas_pure',
    name: 'Pechugas Rellenas con Puré Mixto',
    category: RECIPE_CATEGORIES.TRADICIONAL,
    servings: 2,
    main: {
      componentId: 'comp_pechugas_rellenas',
      componentName: 'Pechugas Rellenas',
      servings: 2
    },
    sides: [
      {
        componentId: 'comp_pure_mixto',
        componentName: 'Puré Mixto',
        servings: 2
      }
    ],
    ingredients: []
  },
  {
    id: 'rec_pescado_zapallo',
    name: 'Filet de Merluza con Puré de Zapallo',
    category: RECIPE_CATEGORIES.SALUDABLE,
    servings: 2,
    main: {
      componentId: 'comp_pescado_horno',
      componentName: 'Pescado al Horno',
      servings: 2
    },
    sides: [
      {
        componentId: 'comp_pure_zapallo',
        componentName: 'Puré de Zapallo',
        servings: 2
      }
    ],
    ingredients: []
  },
  {
    id: 'rec_marineras_rusa',
    name: 'Marineras con Ensalada Rusa',
    category: RECIPE_CATEGORIES.TRADICIONAL,
    servings: 2,
    main: {
      componentId: 'comp_marinera_rebozada',
      componentName: 'Marinera de Pescado',
      servings: 2
    },
    sides: [
      {
        componentId: 'comp_ensalada_rusa',
        componentName: 'Ensalada Rusa',
        servings: 2
      }
    ],
    ingredients: []
  },
  {
    id: 'rec_pollo_crema_fideos',
    name: 'Pechuga a la Crema con Fideos',
    category: RECIPE_CATEGORIES.TRADICIONAL,
    servings: 2,
    main: {
      componentId: 'comp_pollo_grillado',
      componentName: 'Pollo Grillado',
      servings: 2
    },
    sides: [
      {
        componentId: 'comp_salsa_crema',
        componentName: 'Salsa de Crema',
        servings: 1
      },
      {
        componentId: 'comp_fideos',
        componentName: 'Fideos',
        servings: 1
      }
    ],
    ingredients: []
  },
  {
    id: 'rec_albondigas_fideos',
    name: 'Fideos con Albóndigas',
    category: RECIPE_CATEGORIES.TRADICIONAL,
    servings: 4,
    main: {
      componentId: 'comp_albondigas',
      componentName: 'Albóndigas',
      servings: 2
    },
    sides: [
      {
        componentId: 'comp_fideos',
        componentName: 'Fideos',
        servings: 1
      },
      {
        componentId: 'comp_salsa_tomate',
        componentName: 'Salsa de Tomate / Fileto',
        servings: 1
      },
      {
        componentId: 'comp_queso_rallado',
        componentName: 'Queso Rallado',
        servings: 1
      }
    ],
    ingredients: []
  },
  {
    id: 'rec_noquis_crema',
    name: 'Ñoquis con Salsa de Crema',
    category: RECIPE_CATEGORIES.RAPIDA,
    servings: 4,
    main: {
      componentId: 'comp_noquis',
      componentName: 'Ñoquis',
      servings: 1
    },
    sides: [
      {
        componentId: 'comp_salsa_crema',
        componentName: 'Salsa de Crema',
        servings: 1
      },
      {
        componentId: 'comp_queso_rallado',
        componentName: 'Queso Rallado',
        servings: 1
      }
    ],
    ingredients: []
  },
  {
    id: 'rec_spaguetti_pesto_pollo',
    name: 'Spaguetti al Pesto con Tiritas de Pollo',
    category: RECIPE_CATEGORIES.TRADICIONAL,
    servings: 4,
    main: {
      componentId: 'comp_spaguetti',
      componentName: 'Spaguetti',
      servings: 1
    },
    sides: [
      {
        componentId: 'comp_salsa_pesto',
        componentName: 'Salsa Pesto',
        servings: 1
      },
      {
        componentId: 'comp_pollo_grillado',
        componentName: 'Pollo Grillado',
        servings: 2
      },
      {
        componentId: 'comp_queso_rallado',
        componentName: 'Queso Rallado',
        servings: 1
      }
    ],
    ingredients: []
  },
  
  // ==========================================
  // 🍝 PASTAS (12 recetas)
  // ✅ PASTAS + SALSAS SEPARADAS
  // ==========================================
  
  {
    id: 'rec_tallarines_bolognesa',
    name: 'Tallarines con Salsa Bolognesa',
    category: RECIPE_CATEGORIES.TRADICIONAL,
    servings: 4,
    main: {
      componentId: 'comp_tallarines',
      componentName: 'Tallarines',
      servings: 1
    },
    sides: [
      {
        componentId: 'comp_salsa_bolognesa',
        componentName: 'Salsa Bolognesa',
        servings: 1
      },
      {
        componentId: 'comp_queso_rallado',
        componentName: 'Queso Rallado',
        servings: 1
      }
    ],
    ingredients: []
  },
  
  {
    id: 'rec_fideos_salsa_tomate',
    name: 'Fideos con Salsa de Tomate',
    category: RECIPE_CATEGORIES.ECONOMICA,
    servings: 4,
    main: {
      componentId: 'comp_fideos',
      componentName: 'Fideos',
      servings: 1
    },
    sides: [
      {
        componentId: 'comp_salsa_tomate',
        componentName: 'Salsa de Tomate / Fileto',
        servings: 1
      },
      {
        componentId: 'comp_queso_rallado',
        componentName: 'Queso Rallado',
        servings: 1
      }
    ],
    ingredients: []
  },
  
  {
    id: 'rec_spaguetti_pesto',
    name: 'Spaguetti con Pesto',
    category: RECIPE_CATEGORIES.RAPIDA,
    servings: 4,
    main: {
      componentId: 'comp_spaguetti',
      componentName: 'Spaguetti',
      servings: 1
    },
    sides: [
      {
        componentId: 'comp_salsa_pesto',
        componentName: 'Salsa Pesto',
        servings: 1
      },
      {
        componentId: 'comp_queso_rallado',
        componentName: 'Queso Rallado',
        servings: 1
      }
    ],
    ingredients: []
  },
  
  {
    id: 'rec_penne_crema',
    name: 'Penne con Salsa de Crema',
    category: RECIPE_CATEGORIES.TRADICIONAL,
    servings: 4,
    main: {
      componentId: 'comp_penne',
      componentName: 'Penne',
      servings: 1
    },
    sides: [
      {
        componentId: 'comp_salsa_crema',
        componentName: 'Salsa de Crema',
        servings: 1
      },
      {
        componentId: 'comp_queso_rallado',
        componentName: 'Queso Rallado',
        servings: 1
      }
    ],
    ingredients: []
  },
  
  {
    id: 'rec_ravioles_salsa_blanca',
    name: 'Ravioles con Salsa Blanca',
    category: RECIPE_CATEGORIES.TRADICIONAL,
    servings: 4,
    main: {
      componentId: 'comp_ravioles',
      componentName: 'Ravioles',
      servings: 1
    },
    sides: [
      {
        componentId: 'comp_salsa_blanca',
        componentName: 'Salsa Blanca (Bechamel)',
        servings: 1
      },
      {
        componentId: 'comp_queso_rallado',
        componentName: 'Queso Rallado',
        servings: 1
      }
    ],
    ingredients: []
  },
  
  {
    id: 'rec_ravioles_bolognesa',
    name: 'Ravioles con Salsa Bolognesa',
    category: RECIPE_CATEGORIES.TRADICIONAL,
    servings: 4,
    main: {
      componentId: 'comp_ravioles',
      componentName: 'Ravioles',
      servings: 1
    },
    sides: [
      {
        componentId: 'comp_salsa_bolognesa',
        componentName: 'Salsa Bolognesa',
        servings: 1
      },
      {
        componentId: 'comp_queso_rallado',
        componentName: 'Queso Rallado',
        servings: 1
      }
    ],
    ingredients: []
  },
  
  {
    id: 'rec_sorrentinos_crema',
    name: 'Sorrentinos con Salsa de Crema',
    category: RECIPE_CATEGORIES.TRADICIONAL,
    servings: 4,
    main: {
      componentId: 'comp_sorrentinos',
      componentName: 'Sorrentinos',
      servings: 1
    },
    sides: [
      {
        componentId: 'comp_salsa_crema',
        componentName: 'Salsa de Crema',
        servings: 1
      },
      {
        componentId: 'comp_queso_rallado',
        componentName: 'Queso Rallado',
        servings: 1
      }
    ],
    ingredients: []
  },
  
  {
    id: 'rec_sorrentinos_salsa_blanca',
    name: 'Sorrentinos con Salsa Blanca',
    category: RECIPE_CATEGORIES.TRADICIONAL,
    servings: 4,
    main: {
      componentId: 'comp_sorrentinos',
      componentName: 'Sorrentinos',
      servings: 1
    },
    sides: [
      {
        componentId: 'comp_salsa_blanca',
        componentName: 'Salsa Blanca (Bechamel)',
        servings: 1
      }
    ],
    ingredients: []
  },
  
  {
    id: 'rec_noquis_bolognesa',
    name: 'Ñoquis',
    category: RECIPE_CATEGORIES.TRADICIONAL,
    servings: 4,
    main: {
      componentId: 'comp_noquis',
      componentName: 'Ñoquis',
      servings: 1
    },
    sides: [
      {
        componentId: 'comp_salsa_bolognesa',
        componentName: 'Salsa Bolognesa',
        servings: 1
      },
      {
        componentId: 'comp_queso_rallado',
        componentName: 'Queso Rallado',
        servings: 1
      }
    ],
    ingredients: []
  },
  
  {
    id: 'rec_canelones',
    name: 'Canelones de Ricota',
    category: RECIPE_CATEGORIES.TRADICIONAL,
    servings: 4,
    main: {
      componentId: 'comp_canelones',
      componentName: 'Canelones',
      servings: 1
    },
    sides: [
      {
        componentId: 'comp_salsa_blanca',
        componentName: 'Salsa Blanca (Bechamel)',
        servings: 1
      },
      {
        componentId: 'comp_salsa_tomate',
        componentName: 'Salsa de Tomate / Fileto',
        servings: 1
      }
    ],
    ingredients: []
  },
  
  {
    id: 'rec_lasagna',
    name: 'Lasagna',
    category: RECIPE_CATEGORIES.TRADICIONAL,
    servings: 6,
    main: {
      componentId: 'comp_lasagna',
      componentName: 'Lasagna',
      servings: 1
    },
    sides: [
      {
        componentId: 'comp_salsa_blanca',
        componentName: 'Salsa Blanca (Bechamel)',
        servings: 1
      },
      {
        componentId: 'comp_salsa_bolognesa',
        componentName: 'Salsa Bolognesa',
        servings: 1
      }
    ],
    ingredients: []
  },
  
  {
    id: 'rec_mostacholes_hongos',
    name: 'Mostacholes con Salsa de Hongos',
    category: RECIPE_CATEGORIES.TRADICIONAL,
    servings: 4,
    main: {
      componentId: 'comp_mostacholes',
      componentName: 'Mostacholes',
      servings: 1
    },
    sides: [
      {
        componentId: 'comp_salsa_hongos',
        componentName: 'Salsa de Hongos',
        servings: 1
      },
      {
        componentId: 'comp_queso_rallado',
        componentName: 'Queso Rallado',
        servings: 1
      }
    ],
    ingredients: []
  },
{
    id: 'rec_noquis_hongos',
    name: 'Ñoquis con Salsa de Hongos',
    category: RECIPE_CATEGORIES.TRADICIONAL,
    servings: 4,
    main: {
      componentId: 'comp_noquis',
      componentName: 'Ñoquis',
      servings: 1
    },
    sides: [
      {
        componentId: 'comp_salsa_hongos',
        componentName: 'Salsa de Hongos',
        servings: 1
      },
      {
        componentId: 'comp_queso_rallado',
        componentName: 'Queso Rallado',
        servings: 1
      }
    ],
    ingredients: []
  },
  {
    id: 'rec_ravioles_pesto',
    name: 'Ravioles al Pesto',
    category: RECIPE_CATEGORIES.RAPIDA,
    servings: 4,
    main: {
      componentId: 'comp_ravioles',
      componentName: 'Ravioles',
      servings: 1
    },
    sides: [
      {
        componentId: 'comp_salsa_pesto',
        componentName: 'Salsa Pesto',
        servings: 1
      },
      {
        componentId: 'comp_queso_rallado',
        componentName: 'Queso Rallado',
        servings: 1
      }
    ],
    ingredients: []
  },
  {
    id: 'rec_canelones_bolognesa',
    name: 'Canelones con Bolognesa',
    category: RECIPE_CATEGORIES.TRADICIONAL,
    servings: 4,
    main: {
      componentId: 'comp_canelones',
      componentName: 'Canelones',
      servings: 1
    },
    sides: [
      {
        componentId: 'comp_salsa_bolognesa',
        componentName: 'Salsa Bolognesa',
        servings: 1
      },
      {
        componentId: 'comp_queso_rallado',
        componentName: 'Queso Rallado',
        servings: 1
      }
    ],
    ingredients: []
  },
  {
    id: 'rec_spaguetti_portuguesa',
    name: 'Spaguetti con Salsa Portuguesa',
    category: RECIPE_CATEGORIES.VEGETARIANA,
    servings: 4,
    main: {
      componentId: 'comp_spaguetti',
      componentName: 'Spaguetti',
      servings: 1
    },
    sides: [
      {
        componentId: 'comp_salsa_portuguesa',
        componentName: 'Salsa Portuguesa',
        servings: 1
      },
      {
        componentId: 'comp_queso_rallado',
        componentName: 'Queso Rallado',
        servings: 1
      }
    ],
    ingredients: []
  },
  
  // ==========================================
  // 🍲 GUISOS (5 recetas)
  // ==========================================
  
  {
    id: 'rec_guiso_arroz',
    name: 'Guiso de Arroz',
    category: RECIPE_CATEGORIES.ECONOMICA,
    servings: 4,
    main: {
      componentId: 'comp_guiso_arroz',
      componentName: 'Guiso de Arroz',
      servings: 1
    },
    sides: [
      {
        componentId: 'comp_pan_casero',
        componentName: 'Pan Casero',
        servings: 1
      }
    ],
    ingredients: []
  },
  
  {
    id: 'rec_guiso_lentejas',
    name: 'Guiso de Lentejas',
    category: RECIPE_CATEGORIES.ECONOMICA,
    servings: 6,
    main: {
      componentId: 'comp_guiso_lentejas',
      componentName: 'Guiso de Lentejas',
      servings: 1
    },
    sides: [
      {
        componentId: 'comp_pan_casero',
        componentName: 'Pan Casero',
        servings: 1
      }
    ],
    ingredients: []
  },
  
  {
    id: 'rec_locro',
    name: 'Locro',
    category: RECIPE_CATEGORIES.TRADICIONAL,
    servings: 8,
    main: {
      componentId: 'comp_locro',
      componentName: 'Locro',
      servings: 1
    },
    sides: [
      {
        componentId: 'comp_pan_casero',
        componentName: 'Pan Casero',
        servings: 1
      }
    ],
    ingredients: []
  },
  
  {
    id: 'rec_puchero',
    name: 'Puchero',
    category: RECIPE_CATEGORIES.TRADICIONAL,
    servings: 6,
    main: {
      componentId: 'comp_puchero',
      componentName: 'Puchero',
      servings: 1
    },
    sides: [
      {
        componentId: 'comp_pan_casero',
        componentName: 'Pan Casero',
        servings: 1
      }
    ],
    ingredients: []
  },
  
  {
    id: 'rec_carbonada',
    name: 'Carbonada',
    category: RECIPE_CATEGORIES.TRADICIONAL,
    servings: 4,
    main: {
      componentId: 'comp_carbonada',
      componentName: 'Carbonada',
      servings: 1
    },
    sides: [
      {
        componentId: 'comp_pan_casero',
        componentName: 'Pan Casero',
        servings: 1
      }
    ],
    ingredients: []
  },

  // ==========================================
  // 🥬 VEGETARIANAS (4 recetas)
  // ==========================================
  
  {
    id: 'rec_torrejitas_acelga',
    name: 'Torrejitas de Acelga',
    category: RECIPE_CATEGORIES.VEGETARIANA,
    servings: 4,
    main: {
      componentId: 'comp_torrejitas_acelga',
      componentName: 'Torrejitas de Acelga',
      servings: 2
    },
    sides: [
      {
        componentId: 'comp_ensalada_tomate',
        componentName: 'Ensalada de Tomate y Huevo',
        servings: 1
      }
    ],
    ingredients: []
  },
  
  {
    id: 'rec_torrejitas_arroz',
    name: 'Torrejitas de Arroz',
    category: RECIPE_CATEGORIES.VEGETARIANA,
    servings: 4,
    main: {
      componentId: 'comp_torrejitas_arroz',
      componentName: 'Torrejitas de Arroz',
      servings: 2
    },
    sides: [
      {
        componentId: 'comp_ensalada_verde',
        componentName: 'Ensalada Verde',
        servings: 1
      }
    ],
    ingredients: []
  },
  
  {
    id: 'rec_pizza',
    name: 'Pizza de Muzzarella',
    category: RECIPE_CATEGORIES.TRADICIONAL,
    servings: 4,
    main: {
      componentId: 'comp_pizza',
      componentName: 'Pizza de Muzzarella',
      servings: 2
    },
    sides: [
      {
        componentId: 'comp_ensalada_verde',
        componentName: 'Ensalada Verde',
        servings: 1
      }
    ],
    ingredients: []
  },
  
  {
    id: 'rec_tortilla_papa',
    name: 'Tortilla de Papa con Ensalada',
    category: RECIPE_CATEGORIES.VEGETARIANA,
    servings: 4,
    main: {
      componentId: 'comp_tortilla_papa',
      componentName: 'Tortilla de Papa',
      servings: 2
    },
    sides: [
      {
        componentId: 'comp_ensalada_mixta',
        componentName: 'Ensalada Mixta',
        servings: 1
      }
    ],
    ingredients: []
  },
  {
    id: 'rec_tortilla_tomate',
    name: 'Tortilla de Papa con Ensalada de Tomate',
    category: RECIPE_CATEGORIES.VEGETARIANA,
    servings: 4,
    main: {
      componentId: 'comp_tortilla_papa',
      componentName: 'Tortilla de Papa',
      servings: 2
    },
    sides: [
      {
        componentId: 'comp_ensalada_tomate',
        componentName: 'Ensalada de Tomate y Huevo',
        servings: 1
      }
    ],
    ingredients: []
  },
  {
    id: 'rec_verduras_grilladas_zapallo',
    name: 'Verduras Grilladas con Puré de Zapallo',
    category: RECIPE_CATEGORIES.VEGETARIANA,
    servings: 2,
    main: {
      componentId: 'comp_verduras_grilladas',
      componentName: 'Verduras Grilladas',
      servings: 2
    },
    sides: [
      {
        componentId: 'comp_pure_zapallo',
        componentName: 'Puré de Zapallo',
        servings: 2
      }
    ],
    ingredients: []
  },
  {
    id: 'rec_torrejitas_arroz_chaucha',
    name: 'Torrejitas de Arroz con Ensalada de Chauchas',
    category: RECIPE_CATEGORIES.VEGETARIANA,
    servings: 4,
    main: {
      componentId: 'comp_torrejitas_arroz',
      componentName: 'Torrejitas de Arroz',
      servings: 2
    },
    sides: [
      {
        componentId: 'comp_ensalada_chaucha',
        componentName: 'Ensalada de Chaucha',
        servings: 2
      }
    ],
    ingredients: []
  },
  {
    id: 'rec_omelette_choclo',
    name: 'Omelette con Choclo a la Parrilla',
    category: RECIPE_CATEGORIES.VEGETARIANA,
    servings: 1,
    main: {
      componentId: 'comp_omelette',
      componentName: 'Omelette de Jamón y Queso',
      servings: 1
    },
    sides: [
      {
        componentId: 'comp_choclo_parrilla',
        componentName: 'Choclo a la Parrilla',
        servings: 1
      }
    ],
    ingredients: []
  },
  {
    id: 'rec_verduras_grilladas_arroz',
    name: 'Verduras Grilladas con Arroz Integral',
    category: RECIPE_CATEGORIES.VEGETARIANA,
    servings: 2,
    main: {
      componentId: 'comp_verduras_grilladas',
      componentName: 'Verduras Grilladas',
      servings: 2
    },
    sides: [
      {
        componentId: 'comp_arroz_integral',
        componentName: 'Arroz Integral',
        servings: 2
      }
    ],
    ingredients: []
  },
  {
    id: 'rec_omelette_tomate',
    name: 'Omelette con Ensalada de Tomate',
    category: RECIPE_CATEGORIES.RAPIDA,
    servings: 1,
    main: {
      componentId: 'comp_omelette',
      componentName: 'Omelette de Jamón y Queso',
      servings: 1
    },
    sides: [
      {
        componentId: 'comp_ensalada_tomate',
        componentName: 'Ensalada de Tomate y Huevo',
        servings: 1
      }
    ],
    ingredients: []
  },
  
  // ==========================================
  // 🥗 ENSALADAS PRINCIPALES (2 recetas)
  // ==========================================
  
  {
    id: 'rec_ensalada_cesar',
    name: 'Ensalada César con Pollo',
    category: RECIPE_CATEGORIES.SALUDABLE,
    servings: 2,
    main: {
      componentId: 'comp_ensalada_caesar',
      componentName: 'Ensalada César',
      servings: 1
    },
    sides: [
      {
        componentId: 'comp_pollo_grillado',
        componentName: 'Pollo Grillado',
        servings: 1
      },
      {
        componentId: 'comp_pan_ajo',
        componentName: 'Pan de Ajo',
        servings: 1
      }
    ],
    ingredients: []
  },
  
  {
    id: 'rec_ensalada_completa',
    name: 'Ensalada Completa',
    category: RECIPE_CATEGORIES.SALUDABLE,
    servings: 2,
    main: {
      componentId: 'comp_ensalada_verde',
      componentName: 'Ensalada Verde',
      servings: 1
    },
    sides: [
      {
        componentId: 'comp_palta',
        componentName: 'Palta (Ensalada)',
        servings: 1
      },
      {
        componentId: 'comp_tomates_cherry',
        componentName: 'Ensalada de Tomates Cherry',
        servings: 1
      }
    ],
    ingredients: []
  },
  
  // ==========================================
  // ➕ EXTRAS Y PICADAS (1 receta)
  // ==========================================
  
  {
    id: 'rec_picada',
    name: 'Picada',
    category: RECIPE_CATEGORIES.RAPIDA,
    servings: 4,
    main: {
      componentId: 'comp_picada',
      componentName: 'Picada',
      servings: 1
    },
    sides: [],
    ingredients: []
  },
  {
    id: 'rec_panchos',
    name: 'Panchos con Papas Pay',
    category: RECIPE_CATEGORIES.RAPIDA,
    servings: 2,
    main: {
      componentId: 'comp_panchos',
      componentName: 'Panchos',
      servings: 1
    },
    sides: [
      {
        componentId: 'comp_papas_fritas',
        componentName: 'Papas Fritas',
        servings: 1
      }
    ],
    ingredients: []
  },
  // ==========================================
  // 🌯 WRAPS Y WOKS (Nuevos)
  // ==========================================

  {
    id: 'rec_wrap_pollo',
    name: 'Wrap de Pollo',
    category: RECIPE_CATEGORIES.TRADICIONAL, 
    servings: 2,
    main: {
      componentId: 'comp_wrap_pollo',
      componentName: 'Wrap de Pollo (Armado)',
      servings: 1
    },
    sides: [],
    ingredients: []
  },

  {
    id: 'rec_bife_cacerola',
    name: 'Bife a la Cacerola',
    category: RECIPE_CATEGORIES.TRADICIONAL,
    servings: 2,
    main: {
      componentId: 'comp_bife_cacerola',
      componentName: 'Bife a la Cacerola',
      servings: 1
    },
    sides: [],
    ingredients: []
  },

  {
    id: 'rec_wok_fideos',
    name: 'Wok de Fideos',
    category: RECIPE_CATEGORIES.TRADICIONAL,
    servings: 2,
    main: {
      componentId: 'comp_wok_fideos',
      componentName: 'Wok de Fideos y Pollo',
      servings: 1
    },
    sides: [],
    ingredients: []
  }
  
];

// ============================================
// HELPERS
// ============================================

export const generateRecipeName = (mainComponent, sideComponents = []) => {
  if (!mainComponent) return 'Receta sin nombre';
  
  if (sideComponents.length === 0) {
    return mainComponent.name;
  }
  
  if (sideComponents.length === 1) {
    return `${mainComponent.name} con ${sideComponents[0].name}`;
  }
  
  if (sideComponents.length === 2) {
    return `${mainComponent.name} con ${sideComponents[0].name} y ${sideComponents[1].name}`;
  }
  
  return `${mainComponent.name} completo`;
};

export const getRecipeSummary = (recipe) => {
  const parts = [recipe.main.componentName];
  
  if (recipe.sides && recipe.sides.length > 0) {
    const sideNames = recipe.sides.map(s => s.componentName);
    parts.push(...sideNames);
  }
  
  return parts.join(' + ');
};

export const getRecipesByCategory = (category) => {
  return SAMPLE_RECIPES.filter(r => r.category === category);
};

export const searchRecipes = (query) => {
  const lowerQuery = query.toLowerCase();
  return SAMPLE_RECIPES.filter(r => 
    r.name.toLowerCase().includes(lowerQuery)
  );
};

// ============================================
// ESTADÍSTICAS
// ============================================

export const RECIPE_STATS = {
  total: SAMPLE_RECIPES.length,
  byCategory: Object.values(RECIPE_CATEGORIES).reduce((acc, cat) => {
    acc[cat] = SAMPLE_RECIPES.filter(r => r.category === cat).length;
    return acc;
  }, {})
};

// ============================================
// EXPORT DEFAULT
// ============================================

export default {
  RECIPE_CATEGORIES,
  RECIPE_CATEGORY_LABELS,
  RECIPE_CATEGORY_ICONS,
  calculateRecipeIngredients,
  validateRecipe,
  generateRecipeName,
  getRecipeSummary,
  getRecipesByCategory,
  searchRecipes,
  SAMPLE_RECIPES,
  RECIPE_STATS
};

console.log('✅ Recetas v5.1 cargadas:', SAMPLE_RECIPES.length, 'recetas con componentes simplificados - NOMBRES CORREGIDOS');
console.log('📊 Distribución:', RECIPE_STATS.byCategory);